<script>
// AutomationConditionGroup.vue
import { computed, ref } from 'vue';
import FilterInputBox from './Index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'AutomationConditionGroup',
  components: {
    FilterInputBox,
    NextButton,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    groupIndex: {
      type: Number,
      required: true,
    },
    isOnlyGroup: {
      type: Boolean,
      default: false,
    },
    isFirstOperator: {
      type: Boolean,
      default: false,
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    allCustomAttributes: {
      type: Array,
      default: () => [],
    },
    automationTypes: {
      type: Object,
      default: () => ({}),
    },
    automation: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'create',
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    getInputType: {
      type: Function,
      required: true,
    },
    getOperators: {
      type: Function,
      required: true,
    },
    getConditionDropdownValues: {
      type: Function,
      required: true,
    },
    getCustomAttributeType: {
      type: Function,
      required: true,
    },
  },
  emits: [
    'update:modelValue',
    'remove',
    'update:groupOperator',
    'resetFilter',
    'removeFilter',
  ],
  setup(props, { emit }) {
    const isCollapsed = ref(false);

    const group = computed({
      get: () => props.modelValue,
      set: val => emit('update:modelValue', val),
    });

    const conditions = computed({
      get: () => group.value.conditions || [],
      set: val => {
        emit('update:modelValue', { ...group.value, conditions: val });
      },
    });

    const internalOperator = computed({
      get: () => group.value.internalOperator || 'and',
      set: val => {
        emit('update:modelValue', { ...group.value, internalOperator: val });
      },
    });

    const groupOperator = computed({
      get: () => group.value.groupOperator,
      set: val => {
        emit('update:modelValue', { ...group.value, groupOperator: val });
      },
    });

    const filtersSummary = computed(() => {
      const validFilters = conditions.value.filter(f => f.attribute_key);
      if (validFilters.length === 0) return 'Nenhum filtro';

      const names = validFilters.slice(0, 3).map(f => {
        const filterType = props.filterAttributes.find(
          ft => ft.key === f.attribute_key
        );
        return filterType?.name || f.attribute_key;
      });

      const remaining = validFilters.length - 3;
      if (remaining > 0) {
        return `${names.join(', ')} +${remaining}`;
      }
      return names.join(', ');
    });

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    // O evento do link do webchat não tem "status": a linha nova nasce em
    // "caixa de entrada", senão o seletor caía num SEGUNDO prazo.
    const createDefaultCondition = () => ({
      attribute_key:
        props.automation?.event_name === 'webchat_link_unopened'
          ? 'inbox_id'
          : 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: internalOperator.value,
      custom_attribute_type: '',
    });

    const addCondition = () => {
      const newConditions = [...conditions.value, createDefaultCondition()];
      conditions.value = newConditions;
      isCollapsed.value = false;
    };

    const updateCondition = (index, newCondition) => {
      const newConditions = [...conditions.value];
      newConditions[index] = newCondition;
      conditions.value = newConditions;
    };

    const removeCondition = index => {
      if (conditions.value.length === 1) {
        const newConditions = [createDefaultCondition()];
        conditions.value = newConditions;
      } else {
        const newConditions = conditions.value.filter((_, i) => i !== index);
        conditions.value = newConditions;
      }
    };

    const resetCondition = (index, condition) => {
      emit('resetFilter', props.groupIndex, index, condition);
    };

    const onInternalOperatorChange = newOperator => {
      internalOperator.value = newOperator;
      // Propaga para todas as condições do grupo
      const newConditions = conditions.value.map(c => ({
        ...c,
        query_operator: newOperator,
      }));
      conditions.value = newConditions;
    };

    const removeGroup = () => {
      emit('remove', props.groupIndex);
    };

    const selectAnd = () => {
      if (props.isFirstOperator) {
        emit('update:groupOperator', 'and');
      }
    };

    const selectOr = () => {
      if (props.isFirstOperator) {
        emit('update:groupOperator', 'or');
      }
    };

    return {
      isCollapsed,
      conditions,
      internalOperator,
      groupOperator,
      filtersSummary,
      toggleCollapse,
      addCondition,
      updateCondition,
      removeCondition,
      resetCondition,
      onInternalOperatorChange,
      removeGroup,
      selectAnd,
      selectOr,
    };
  },
};
</script>

<template>
  <div class="flex items-start gap-2">
    <!-- Label "Onde" ou Toggle E/OU -->
    <div class="pt-3 min-w-[60px]">
      <span
        v-if="groupIndex === 0"
        class="text-xs text-n-slate-11 font-medium"
      >
        {{ $t('AUTOMATION.CONDITION.WHERE') }}
      </span>
      <div
        v-else
        class="inline-flex items-center rounded-md bg-n-slate-2 border border-n-weak overflow-hidden w-full"
        :class="{ 'opacity-60': !isFirstOperator }"
      >
        <button
          type="button"
          :disabled="!isFirstOperator"
          class="flex-1 px-2 py-1 text-xs font-medium transition-colors duration-150 text-center"
          :class="[
            groupOperator === 'and' ? 'bg-n-blue-9 text-white' : 'text-n-slate-11',
            isFirstOperator && groupOperator !== 'and' ? 'hover:bg-n-slate-3' : '',
            !isFirstOperator ? 'cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="selectAnd"
        >
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
        </button>
        <div class="w-px h-4 bg-n-weak" />
        <button
          type="button"
          :disabled="!isFirstOperator"
          class="flex-1 px-2 py-1 text-xs font-medium transition-colors duration-150 text-center"
          :class="[
            groupOperator === 'or' ? 'bg-n-blue-9 text-white' : 'text-n-slate-11',
            isFirstOperator && groupOperator !== 'or' ? 'hover:bg-n-slate-3' : '',
            !isFirstOperator ? 'cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="selectOr"
        >
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
        </button>
      </div>
    </div>

    <!-- Grupo -->
    <div
      class="condition-group border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong flex-1"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-n-slate-3 transition-colors select-none rounded-t-lg"
        :class="{ 'border-b border-solid border-n-weak': !isCollapsed }"
        @click="toggleCollapse"
      >
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <!-- Ícone de expansão -->
          <span
            class="text-n-slate-11 transition-transform duration-200 flex-shrink-0"
            :class="[
              isCollapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down',
            ]"
          />

          <!-- Título do grupo -->
          <span
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide flex-shrink-0"
          >
            {{ $t('AUTOMATION.CONDITION.GROUP_LABEL') }} {{ groupIndex + 1 }}
          </span>

          <!-- Badge com contagem -->
          <span
            class="px-1.5 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-11 flex-shrink-0"
          >
            {{ conditions.length }}
            {{
              conditions.length === 1
                ? $t('AUTOMATION.CONDITION.CONDITION_SINGULAR')
                : $t('AUTOMATION.CONDITION.CONDITION_PLURAL')
            }}
          </span>

          <!-- Resumo quando colapsado -->
          <span
            v-if="isCollapsed"
            class="text-xs text-n-slate-10 truncate"
            :title="filtersSummary"
          >
            {{ filtersSummary }}
          </span>
        </div>

        <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
          <NextButton
            v-if="!isOnlyGroup"
            v-tooltip.top="$t('AUTOMATION.CONDITION.DELETE_GROUP')"
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            @click="removeGroup"
          />
        </div>
      </div>

      <!-- Conteúdo -->
      <div v-show="!isCollapsed" class="p-4 pt-3">
        <div class="flex flex-col gap-2">
          <FilterInputBox
            v-for="(condition, index) in conditions"
            :key="`${groupIndex}-${index}`"
            :model-value="condition"
            :filter-attributes="filterAttributes"
            :input-type="
              getInputType(
                allCustomAttributes,
                automationTypes,
                automation,
                condition.attribute_key
              )
            "
            :operators="
              getOperators(
                allCustomAttributes,
                automationTypes,
                automation,
                mode,
                condition.attribute_key
              )
            "
            :dropdown-values="getConditionDropdownValues(condition.attribute_key)"
            :custom-attribute-type="
              getCustomAttributeType(
                automationTypes,
                automation,
                condition.attribute_key
              )
            "
            :show-query-operator="index !== conditions.length - 1"
            :is-first-query-operator="index === 0"
            :error-message="
              errors[`condition_${groupIndex}_${index}`]
                ? $t(`AUTOMATION.ERRORS.${errors[`condition_${groupIndex}_${index}`]}`)
                : ''
            "
            @update:model-value="updateCondition(index, $event)"
            @reset-filter="resetCondition(index, condition)"
            @remove-filter="removeCondition(index)"
            @update-query-operator="onInternalOperatorChange"
          />
        </div>

        <div class="mt-3">
          <NextButton
            sm
            ghost
            blue
            icon="i-lucide-plus"
            :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
            @click="addCondition"
          />
        </div>
      </div>
    </div>
  </div>
</template>
